import React from "react"
import Toggle from "material-ui/Toggle"
import AccountStore from "../../../stores/AccountStore"
import strings from "../../../localization/strings"
import TimecardWeekTable from "../TimecardWeekTable"
import autobind from "class-autobind"
import TimecardDailyDrawer from "../daily_drawer/TimecardDailyDrawer"
import { IDate, ITimecard, ITimecardDay, ITimecardFullData, IObjectTimecardDocument, ITimecardEmployee, IPunchSetDocument, IPayrollPayCalendar, IJobDocument } from "../../../models"
import * as timecardHelper from "../../../helpers/timecardHelper"
import AppStateStore from "../../../stores/AppStateStore"
import AuthenticationStore from "../../../stores/AuthenticationStore"
import * as permissionHelper from "../../../helpers/permissionHelper"
import * as approveActions from "../../../actions/ApproveActions"
import HierarchyJobTimezoneStr from "../../common/HierarchyJobTimezoneStr"
import { checkJobFieldAvailable } from "../../../helpers/jobHelper"
import { checkPhaseFieldAvailable } from "../../../helpers/punchHelper"

interface IProps {
    showTimcard: boolean
    dailyNoteDates: IDate[]
    timecard: ITimecardFullData
    employee: ITimecardEmployee
    payrollPayCalendar: IPayrollPayCalendar
    readonly: boolean
    filterByPunchHierarchy: boolean
    handleApproveStatus(timecard: ITimecardFullData, status: string, dayOfWeekIndex: number): void
    onAddPunchToTimecardDay(timecardDay: ITimecardDay, user: ITimecardEmployee, timecard: IObjectTimecardDocument, hierarchyItemId: string, positionId: string, payClassId: string, payTypeId: string, shiftId: string, breakId: string, mobileBreakId: string, jobId: string, phaseId: string, equipmentId: string, isPunchIn: boolean, pairedPunchId: string): void
    onTimeChange(punchId: string, dateTime: Date | string, errorCb: () => void): void
    onConfirmDelete(punchId: string, timecard: ITimecardFullData): void
    reloadTimecard(): void

    // if Job->Timecard, it's not full timecard object. so let's hide Info icon to show Notes, Audits and WorkHistory.
    hideInfoIcon?: boolean
    showTimecardDetails: boolean

    // job -> timecard list
    job?: IJobDocument
}
interface IState {
    showComponentTimecard: boolean
    showProductiveNonProductiveTime: boolean
    dailyDrawerDate: number
}

export default class TimecardInnerTable extends React.Component<IProps, IState> {
    constructor(props) {
        super(props)
        this.state = {
            showComponentTimecard: false,
            showProductiveNonProductiveTime: false,
            dailyDrawerDate: null
        }
        autobind(this)
    }

    onToggleShowComponentTimecard(event, value) {
        const currTop = window.scrollY
        const currLeft = window.scrollX
        this.setState({
            showComponentTimecard: value,
            showProductiveNonProductiveTime: value ? false : this.state.showProductiveNonProductiveTime
        }, () =>
            window.scrollTo(currLeft, currTop)
        )
    }

    onToggleShowProductiveNonProductiveTime(event, value) {
        const currTop = window.scrollY
        const currLeft = window.scrollX
        this.setState({
            showProductiveNonProductiveTime: value,
            showComponentTimecard: value ? false : this.state.showComponentTimecard
        }, () =>
            window.scrollTo(currLeft, currTop)
        )
    }

    openDailyHistoryDrawer(date) {
        this.setState({ dailyDrawerDate: date })
    }

    closeDailyHistoryDrawer() {
        this.setState({ dailyDrawerDate: null })
    }

    handleApproveStatus(status, dayOfWeekIndex) {
        this.props.handleApproveStatus ?
            this.props.handleApproveStatus(this.props.timecard, status, dayOfWeekIndex) :
            null
    }

    getJobPhaseLabel(timecard: ITimecard) {
        let jobPhaseLabel
        if (timecard.JobId) {
            jobPhaseLabel = ` ${checkJobFieldAvailable("singular") ? AccountStore.getCurrentAccountSettings().CustomFieldSettings.JobFieldLabels.SingularJobLabel : strings.Job}: ${timecard.JobName}`
        }

        if (timecard.JobId && timecard.PhaseId) {
            jobPhaseLabel += `${checkPhaseFieldAvailable("singular") ? AccountStore.getCurrentAccountSettings().CustomFieldSettings.PhaseFieldLabels.SingularPhaseLabel : strings.Phase}: ${timecard.PhaseName}`
        }
        return jobPhaseLabel
    }

    handleApprovePunchSet(status, punchSet: IPunchSetDocument, timecard: ITimecardFullData) {
        // supervisor cannot approve/unapprove his own timecard
        if (timecard.Timecard.Employee.Id === AuthenticationStore.getLoggedInUser().Id) {
            AppStateStore.setAlertDialogContent({
                title: strings.CannotApproveTimecardsTitle,
                content: status === "approve" ? strings.Supervisor_cannot_approve_his_own_timecard : strings.Supervisor_cannot_unapprove_his_own_timecard
            })
            return
        }

        if (!permissionHelper.hasPermission(AuthenticationStore.Permissions.Timecards.ApproveTimecards))
            return

        AppStateStore.setConfirmDialogContent({
            title: status === "approve" ? strings.Approve_PunchSet : strings.Unapprove_PunchSet,
            content: status === "approve" ? strings.Are_you_sure_you_want_to_approve_punchSet : strings.Are_you_sure_you_want_to_unapprove_punchSet,
            confirm: () => status === "approve" ?
                approveActions.approvePunchSet(punchSet, this.props.reloadTimecard) :
                approveActions.unApprovePunchSet(punchSet, this.props.reloadTimecard)
        })
    }

    render() {
        const timecardToggles =
            <div>
                <div
                    style={{ maxWidth: 300, float: "right", margin: 10 }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {this.props.timecard.Timecard.ComponentTimecards && this.props.timecard.Timecard.ComponentTimecards.length > 1 &&
                        <Toggle
                            label={strings.ShowComponentTimecards}
                            onToggle={this.onToggleShowComponentTimecard}
                            toggled={this.state.showComponentTimecard}
                        />
                    }
                    {this.props.timecard.Timecard.ComponentTimecards && this.props.timecard.Timecard.ComponentTimecards.length > 0 &&
                        <Toggle
                            label={strings.Show_Productive_Non_Productive_Time}
                            onToggle={this.onToggleShowProductiveNonProductiveTime}
                            toggled={this.state.showProductiveNonProductiveTime}
                        />
                    }
                </div>
                <div style={{ clear: "both" }} />
            </div >
        const handleApproveStatus = this.props.handleApproveStatus ?
            (status, dayOfWeekIndex) => this.props.handleApproveStatus(this.props.timecard, status, dayOfWeekIndex) :
            null
        return (
            <div>
                {this.props.showTimcard && (
                    this.state.showComponentTimecard ?
                        <div>
                            {timecardToggles}
                            {this.props.timecard.Timecard.ComponentTimecards.map((componentTimecard, index) =>
                                <div
                                    style={{ borderTop: "solid 1px #efefef", paddingTop: 10 }}
                                    key={index}
                                >
                                    <b>{`${componentTimecard.HierarchyItem ? componentTimecard.HierarchyItem.Label : AccountStore.getCurrentAccount().Name}${componentTimecard.Position ? ": " : ""}`}</b>
                                    {timecardHelper.getTimecardPositionLabel(componentTimecard)}
                                    {this.getJobPhaseLabel(componentTimecard)}
                                    <div style={{ float: "right", marginRight: 10 }}>
                                        <i>{strings.Hierarchy_Timezone}:&nbsp;
                                            <HierarchyJobTimezoneStr
                                                hierarchyId={componentTimecard.HierarchyItem ? componentTimecard.HierarchyItem.Id : null}
                                                jobId={componentTimecard.JobId}
                                                utcTime={this.props.timecard.Timecard.StartDateUTC}
                                            />
                                        </i>
                                    </div>
                                    {componentTimecard.TimecardWeeks.map((timecardWeek, index1) => {
                                        return (
                                            <TimecardWeekTable
                                                filterByPunchHierarchy={this.props.filterByPunchHierarchy}
                                                dailyNoteDates={this.props.dailyNoteDates}
                                                timecard={this.props.timecard.Timecard}
                                                payrollDataForPayGroup={this.props.payrollPayCalendar}
                                                lockData={this.props.timecard.PayrollStatus}
                                                timecardWeek={timecardWeek}
                                                employee={this.props.employee}
                                                onAddPunchToTimecardDay={this.props.onAddPunchToTimecardDay}
                                                onTimeChange={this.props.onTimeChange}
                                                onConfirmDelete={punchId => this.props.onConfirmDelete(punchId, this.props.timecard)}
                                                readonly={this.props.readonly}
                                                hierarchyItemId={componentTimecard.HierarchyItem ? componentTimecard.HierarchyItem.Id : null}
                                                jobId={componentTimecard.JobId}
                                                phaseId={componentTimecard.PhaseId}
                                                positionId={componentTimecard.Position ? componentTimecard.Position.Id : null}
                                                key={index1}
                                                openDailyHistoryDrawer={this.openDailyHistoryDrawer}
                                                reloadTimecard={this.props.reloadTimecard}
                                                handleApproveStatus={handleApproveStatus}
                                                showExpenseRow={false}
                                                hideInfoIcon={this.props.hideInfoIcon}
                                                handleApprovePunchSet={(status, punchSet) => this.handleApprovePunchSet(status, punchSet, this.props.timecard)}
                                                jobStatusData={this.props.timecard.JobStatusData}
                                                commentData={this.props.timecard.CommentData}
                                                showTimecardDetails={this.props.showTimecardDetails}
                                                payrollHierarchyId={this.props.timecard.Timecard.PayrollHierarchyId}
                                                payrollPayGroupId={this.props.timecard.Timecard.PayrollPayGroupId}
                                            />
                                        )
                                    })}
                                </div>
                            )}
                        </div> :
                        <div>
                            {this.props.timecard.Timecard && this.props.timecard.Timecard.ComponentTimecards && this.props.timecard.Timecard.ComponentTimecards.length > 0 &&
                                <div>
                                    <div style={!this.props.timecard.Timecard.ExcludeFromTimecardReview && this.props.timecard.Timecard.ComponentTimecards.length === 1 ? { paddingTop: 20 } : { borderTop: "solid 1px #efefef", paddingTop: 10 }} />
                                    {timecardToggles}
                                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                                        <div>
                                            <b>{!this.props.timecard.Timecard.ExcludeFromTimecardReview && this.props.timecard.Timecard.ComponentTimecards.length === 1 && `${this.props.timecard.Timecard.ComponentTimecards[0].HierarchyItem ? this.props.timecard.Timecard.ComponentTimecards[0].HierarchyItem.Label : AccountStore.getCurrentAccount().Name}${this.props.timecard.Timecard.ComponentTimecards[0].Position ? ": " : ""}`}</b>
                                            {!this.props.timecard.Timecard.ExcludeFromTimecardReview && this.props.timecard.Timecard.ComponentTimecards.length === 1 && timecardHelper.getTimecardPositionLabel(this.props.timecard.Timecard.ComponentTimecards[0])}
                                            {!this.props.timecard.Timecard.ExcludeFromTimecardReview && this.props.timecard.Timecard.ComponentTimecards.length === 1 && this.getJobPhaseLabel(this.props.timecard.Timecard.ComponentTimecards[0])}
                                        </div>
                                        <div style={{ marginRight: 10 }}>
                                            <i>
                                                {strings.Hierarchy_Timezone}:&nbsp;
                                                <HierarchyJobTimezoneStr
                                                    hierarchyId={!this.props.timecard.Timecard.ExcludeFromTimecardReview && this.props.timecard.Timecard.ComponentTimecards[0].HierarchyItem ? this.props.timecard.Timecard.ComponentTimecards[0].HierarchyItem.Id : null}
                                                    jobId={!this.props.timecard.Timecard.ExcludeFromTimecardReview && this.props.timecard.Timecard.ComponentTimecards[0] ? this.props.timecard.Timecard.ComponentTimecards[0].JobId : null}
                                                    utcTime={this.props.timecard.Timecard.StartDateUTC}
                                                />
                                            </i>
                                        </div>
                                    </div>
                                </div>
                            }
                            {this.state.showProductiveNonProductiveTime ?
                                (() => {
                                    const productiveTimecard = timecardHelper.getProductiveTimecard(this.props.timecard.Timecard)
                                    const nonProductiveTimecard = timecardHelper.getNonProductiveTimecard(this.props.timecard.Timecard)
                                    return (
                                        <div>
                                            <div style={{ borderTop: "solid 1px #efefef", paddingTop: 10 }}>
                                                <b>{strings.Productive_Time}</b>
                                                <TimecardWeekTable
                                                    filterByPunchHierarchy={this.props.filterByPunchHierarchy}
                                                    dailyNoteDates={this.props.dailyNoteDates}
                                                    timecard={this.props.timecard.Timecard}
                                                    payrollDataForPayGroup={this.props.payrollPayCalendar}
                                                    lockData={this.props.timecard.PayrollStatus}
                                                    timecardWeek={productiveTimecard.TimecardWeeks[0]}
                                                    employee={this.props.employee}
                                                    onAddPunchToTimecardDay={this.props.onAddPunchToTimecardDay}
                                                    onTimeChange={this.props.onTimeChange}
                                                    onConfirmDelete={punchId => this.props.onConfirmDelete(punchId, this.props.timecard)}
                                                    readonly={this.props.readonly}
                                                    hierarchyItemId={
                                                        this.props.timecard.Timecard.ComponentTimecards &&
                                                            this.props.timecard.Timecard.ComponentTimecards.length === 1 ?
                                                            this.props.timecard.Timecard.ComponentTimecards[0].HierarchyItem ?
                                                                this.props.timecard.Timecard.ComponentTimecards[0].HierarchyItem.Id :
                                                                null :
                                                            this.props.job ?
                                                                this.props.job.HierarchyId :
                                                                this.props.timecard.Timecard.Employee.DefaultPunchHierarchyId
                                                    }
                                                    jobId={
                                                        this.props.timecard.Timecard.ComponentTimecards && this.props.timecard.Timecard.ComponentTimecards.length === 1 ?
                                                            this.props.timecard.Timecard.ComponentTimecards[0].JobId :
                                                            this.props.job ?
                                                                this.props.job.Id :
                                                                this.props.timecard.Timecard.Employee.DefaultJobId
                                                    }
                                                    phaseId={
                                                        this.props.timecard.Timecard.ComponentTimecards && this.props.timecard.Timecard.ComponentTimecards.length === 1 ?
                                                            this.props.timecard.Timecard.ComponentTimecards[0].PhaseId :
                                                            this.props.job ?
                                                                null :
                                                                this.props.timecard.Timecard.Employee.DefaultPhaseId
                                                    }
                                                    positionId={
                                                        this.props.timecard.Timecard.ComponentTimecards &&
                                                            this.props.timecard.Timecard.ComponentTimecards.length === 1 ?
                                                            this.props.timecard.Timecard.ComponentTimecards[0].Position ?
                                                                this.props.timecard.Timecard.ComponentTimecards[0].Position.Id :
                                                                null :
                                                            this.props.job ?
                                                                null :
                                                                this.props.timecard.Timecard.Employee.DefaultPositionId
                                                    }
                                                    openDailyHistoryDrawer={this.openDailyHistoryDrawer}
                                                    reloadTimecard={this.props.reloadTimecard}
                                                    handleApproveStatus={handleApproveStatus}
                                                    showExpenseRow={false}
                                                    hideInfoIcon={this.props.hideInfoIcon}
                                                    handleApprovePunchSet={(status, punchSet) => this.handleApprovePunchSet(status, punchSet, this.props.timecard)}
                                                    jobStatusData={this.props.timecard.JobStatusData}
                                                    commentData={this.props.timecard.CommentData}
                                                    showTimecardDetails={this.props.showTimecardDetails}
                                                    payrollHierarchyId={this.props.timecard.Timecard.PayrollHierarchyId}
                                                    payrollPayGroupId={this.props.timecard.Timecard.PayrollPayGroupId}
                                                />
                                            </div>
                                            <div style={{ borderTop: "solid 1px #efefef", paddingTop: 10 }}>
                                                <b>{strings.Non_Productive_Time}</b>
                                                <TimecardWeekTable
                                                    filterByPunchHierarchy={this.props.filterByPunchHierarchy}
                                                    dailyNoteDates={this.props.dailyNoteDates}
                                                    timecard={this.props.timecard.Timecard}
                                                    payrollDataForPayGroup={this.props.payrollPayCalendar}
                                                    lockData={this.props.timecard.PayrollStatus}
                                                    timecardWeek={nonProductiveTimecard.TimecardWeeks[0]}
                                                    employee={this.props.employee}
                                                    onAddPunchToTimecardDay={this.props.onAddPunchToTimecardDay}
                                                    onTimeChange={this.props.onTimeChange}
                                                    onConfirmDelete={punchId => this.props.onConfirmDelete(punchId, this.props.timecard)}
                                                    readonly={this.props.readonly}
                                                    hierarchyItemId={
                                                        this.props.timecard.Timecard.ComponentTimecards &&
                                                            this.props.timecard.Timecard.ComponentTimecards.length === 1 ?
                                                            this.props.timecard.Timecard.ComponentTimecards[0].HierarchyItem ?
                                                                this.props.timecard.Timecard.ComponentTimecards[0].HierarchyItem.Id :
                                                                null :
                                                            this.props.job ?
                                                                this.props.job.HierarchyId :
                                                                this.props.timecard.Timecard.Employee.DefaultPunchHierarchyId
                                                    }
                                                    jobId={
                                                        this.props.timecard.Timecard.ComponentTimecards && this.props.timecard.Timecard.ComponentTimecards.length === 1 ?
                                                            this.props.timecard.Timecard.ComponentTimecards[0].JobId :
                                                            this.props.job ?
                                                                this.props.job.Id :
                                                                this.props.timecard.Timecard.Employee.DefaultJobId
                                                    }
                                                    phaseId={
                                                        this.props.timecard.Timecard.ComponentTimecards && this.props.timecard.Timecard.ComponentTimecards.length === 1 ?
                                                            this.props.timecard.Timecard.ComponentTimecards[0].PhaseId :
                                                            this.props.job ?
                                                                null :
                                                                this.props.timecard.Timecard.Employee.DefaultPhaseId
                                                    }
                                                    positionId={
                                                        this.props.timecard.Timecard.ComponentTimecards &&
                                                            this.props.timecard.Timecard.ComponentTimecards.length === 1 ?
                                                            this.props.timecard.Timecard.ComponentTimecards[0].Position ?
                                                                this.props.timecard.Timecard.ComponentTimecards[0].Position.Id :
                                                                null :
                                                            this.props.job ?
                                                                null :
                                                                this.props.timecard.Timecard.Employee.DefaultPositionId
                                                    }
                                                    openDailyHistoryDrawer={this.openDailyHistoryDrawer}
                                                    reloadTimecard={this.props.reloadTimecard}
                                                    handleApproveStatus={handleApproveStatus}
                                                    showExpenseRow={false}
                                                    hideInfoIcon={this.props.hideInfoIcon}
                                                    handleApprovePunchSet={(status, punchSet) => this.handleApprovePunchSet(status, punchSet, this.props.timecard)}
                                                    jobStatusData={this.props.timecard.JobStatusData}
                                                    commentData={this.props.timecard.CommentData}
                                                    showTimecardDetails={this.props.showTimecardDetails}
                                                    payrollHierarchyId={this.props.timecard.Timecard.PayrollHierarchyId}
                                                    payrollPayGroupId={this.props.timecard.Timecard.PayrollPayGroupId}
                                                />
                                            </div>
                                        </div>
                                    )
                                })() :
                                this.props.timecard.Timecard.MasterTimecard.TimecardWeeks.map((timecardWeek, index) =>
                                    <TimecardWeekTable
                                        filterByPunchHierarchy={this.props.filterByPunchHierarchy}
                                        dailyNoteDates={this.props.dailyNoteDates}
                                        timecard={this.props.timecard.Timecard}
                                        payrollDataForPayGroup={this.props.payrollPayCalendar}
                                        lockData={this.props.timecard.PayrollStatus}
                                        timecardWeek={timecardWeek}
                                        employee={this.props.employee}
                                        onAddPunchToTimecardDay={this.props.onAddPunchToTimecardDay}
                                        onTimeChange={this.props.onTimeChange}
                                        onConfirmDelete={punchId => this.props.onConfirmDelete(punchId, this.props.timecard)}
                                        readonly={this.props.readonly}
                                        hierarchyItemId={
                                            this.props.timecard.Timecard.ComponentTimecards &&
                                                this.props.timecard.Timecard.ComponentTimecards.length === 1 ?
                                                this.props.timecard.Timecard.ComponentTimecards[0].HierarchyItem ?
                                                    this.props.timecard.Timecard.ComponentTimecards[0].HierarchyItem.Id :
                                                    null :
                                                this.props.job ?
                                                    this.props.job.HierarchyId :
                                                    this.props.timecard.Timecard.Employee.DefaultPunchHierarchyId
                                        }
                                        jobId={
                                            this.props.timecard.Timecard.ComponentTimecards && this.props.timecard.Timecard.ComponentTimecards.length === 1 ?
                                                this.props.timecard.Timecard.ComponentTimecards[0].JobId :
                                                this.props.job ?
                                                    this.props.job.Id :
                                                    this.props.timecard.Timecard.Employee.DefaultJobId
                                        }
                                        phaseId={
                                            this.props.timecard.Timecard.ComponentTimecards && this.props.timecard.Timecard.ComponentTimecards.length === 1 ?
                                                this.props.timecard.Timecard.ComponentTimecards[0].PhaseId :
                                                this.props.job ?
                                                    null :
                                                    this.props.timecard.Timecard.Employee.DefaultPhaseId
                                        }
                                        positionId={
                                            this.props.timecard.Timecard.ComponentTimecards &&
                                                this.props.timecard.Timecard.ComponentTimecards.length === 1 ?
                                                this.props.timecard.Timecard.ComponentTimecards[0].Position ?
                                                    this.props.timecard.Timecard.ComponentTimecards[0].Position.Id :
                                                    null :
                                                this.props.job ?
                                                    null :
                                                    this.props.timecard.Timecard.Employee.DefaultPositionId
                                        }
                                        key={index}
                                        openDailyHistoryDrawer={this.openDailyHistoryDrawer}
                                        reloadTimecard={this.props.reloadTimecard}
                                        handleApproveStatus={handleApproveStatus}
                                        showExpenseRow
                                        hideInfoIcon={this.props.hideInfoIcon}
                                        handleApprovePunchSet={(status, punchSet) => this.handleApprovePunchSet(status, punchSet, this.props.timecard)}
                                        jobStatusData={this.props.timecard.JobStatusData}
                                        commentData={this.props.timecard.CommentData}
                                        showTimecardDetails={this.props.showTimecardDetails}
                                        payrollHierarchyId={this.props.timecard.Timecard.PayrollHierarchyId}
                                        payrollPayGroupId={this.props.timecard.Timecard.PayrollPayGroupId}
                                    />
                                )}
                        </div>
                )
                }
                <TimecardDailyDrawer
                    byEmployee
                    enableBreadcrumb={this.props.employee.EnableBreadcrumb}
                    dayNum={this.state.dailyDrawerDate}
                    imageS3Url={this.props.employee.ProfileImageS3Url}
                    timecard={this.props.timecard.Timecard}
                    userId={this.props.timecard.Timecard.Employee.Id || null}
                    objectTimecardId={this.props.timecard && this.props.timecard.Timecard && this.props.timecard.Timecard.Id}
                    timecardDates={{ StartDate: this.props.timecard.Timecard.StartDate, EndDate: this.props.timecard.Timecard.EndDate }}
                    closeDailyDrawer={this.closeDailyHistoryDrawer}
                />
            </div>
        )
    }
}